// Session helpers for the customer login token.
//
// Backend signs customer JWTs with a 30 day expiry (middleware/auth.js).
// Nothing on the client ever looked at that expiry: `isLoggedIn` was simply
// `!!localStorage.token`, so a customer returning after a month still rendered
// as signed in — header avatar, checkout page, the lot — while every authed
// query silently failed. Their name/phone/addresses came back empty and the
// only feedback was a raw "Unauthenticated" toast when they hit Place Order.
//
// These helpers let the app notice an expired/rejected token and turn it into
// an ordinary signed-out state with a clear message instead.

export const SESSION_EXPIRED_EVENT = "rmf:session-expired";

// Decodes a JWT payload without verifying it. Verification is the server's job;
// we only need `exp` to avoid rendering a signed-in shell we know is dead.
export function decodeToken(token) {
  if (!token || typeof token !== "string") return null;
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

// True when the token is unusable: malformed, or past its `exp`.
// A 30 second skew keeps us from sending a token that dies in flight.
export function isTokenExpired(token, skewSeconds = 30) {
  if (!token) return true;
  const payload = decodeToken(token);
  // Not a JWT we can read — let the server be the judge rather than logging
  // the customer out over a parsing quirk.
  if (!payload || typeof payload.exp !== "number") return false;
  return payload.exp <= Date.now() / 1000 + skewSeconds;
}

// The backend rejects unauthenticated calls with `throw new Error('Unauthenticated')`
// (loyaltyPoints.js spells the same check "Unauthorized").
//
// Matched as a WHOLE message on purpose. Resolvers also throw ownership errors
// that merely contain the word — "Unauthorized" after an ownerId mismatch, say —
// and those mean the token was accepted. Signing someone out over one of those
// would be a worse bug than the one this fixes.
const AUTH_ERROR_MESSAGES = /^(unauthenticated|unauthori[sz]ed|jwt (expired|malformed)|invalid token)\.?$/i;

export function isAuthError(graphQLError) {
  if (!graphQLError) return false;
  if (graphQLError.extensions?.code === "UNAUTHENTICATED") return true;
  return AUTH_ERROR_MESSAGES.test(String(graphQLError.message || "").trim());
}

// Fired from the Apollo error link, which lives outside React and so cannot
// touch UserContext directly.
export function notifySessionExpired() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}
